import { useCallback, useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiDownload,
  FiFileText,
  FiMail,
  FiPrinter,
  FiUser,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

function numberValue(value) {
  return Number(value || 0);
}

function formatMoney(value) {
  return `₹${numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMonth(year, month) {
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function formatDate(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function readableValue(value) {
  if (!value) return "—";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function adjustmentLabel(description) {
  if (
    /^EMPLOYEE_ADVANCE:[0-9a-f-]{36}\s*\|\s*Advance recovery$/i.test(
      String(description || ""),
    )
  ) {
    return "Employee advance recovery";
  }

  return description || "Payroll adjustment";
}

function statusLabel(status) {
  const labels = {
    DRAFT: "Draft",
    POSTED: "Ready to Pay",
    PAID: "Paid",
    VOIDED: "Voided",
  };

  return labels[status] || status;
}

export default function PayslipDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [payslip, setPayslip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [error, setError] = useState("");

  const loadPayslip = useCallback(async () => {
    if (!accessToken || !id) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest(`/payroll/payslips/${id}`, {
        accessToken,
      });

      setPayslip(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load payslip.");
      setPayslip(null);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadPayslip();
  }, [loadPayslip]);

  const markPaid = async () => {
    if (!payslip || !accessToken) return;

    const confirmed = window.confirm(
      `Mark ${payslip.employeeNameSnapshot}'s payslip as paid?`,
    );

    if (!confirmed) return;

    try {
      setIsMarkingPaid(true);
      setError("");

      await apiRequest(`/payroll/items/${payslip.id}/mark-paid`, {
        method: "POST",
        accessToken,
        body: {},
      });

      await loadPayslip();
    } catch (requestError) {
      setError(requestError.message || "Unable to mark payslip as paid.");
    } finally {
      setIsMarkingPaid(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-3xl border border-gray-100 bg-white py-24 text-center text-gray-500">
        Loading payslip...
      </div>
    );
  }

  if (!payslip) {
    return (
      <div className="rounded-3xl border border-red-100 bg-red-50 p-8">
        <h1 className="text-xl font-bold text-red-800">Payslip unavailable</h1>

        <p className="mt-2 text-sm text-red-700">
          {error || "The selected payslip could not be found."}
        </p>

        <button
          onClick={() => navigate("/admindashboard/payroll/payslips")}
          className="mt-6 rounded-xl bg-primary-700 px-5 py-3 font-medium text-white"
        >
          Back to Payslips
        </button>
      </div>
    );
  }

  const payrollMonth = formatMonth(
    payslip.payrollRun.periodYear,
    payslip.payrollRun.periodMonth,
  );

  const manualEarnings = numberValue(payslip.manualEarningAmount);
  const manualDeductions = numberValue(payslip.manualDeductionAmount);

  const advanceRecoveryAmount = (payslip.adjustments || [])
    .filter((adjustment) =>
      String(adjustment.description || "").startsWith("EMPLOYEE_ADVANCE:"),
    )
    .reduce((sum, adjustment) => sum + numberValue(adjustment.amount), 0);

  const manualDeductionsExcludingAdvance = Math.max(
    0,
    manualDeductions - advanceRecoveryAmount,
  );

  const payoutBeforeAdvanceRecovery =
    numberValue(payslip.netPayable) + advanceRecoveryAmount;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/payroll/payslips")}
            className="inline-flex items-center gap-2 font-medium text-primary-700 hover:text-primary-800"
          >
            <FiArrowLeft />
            Back to Payslips
          </button>

          <h1 className="mt-4 text-3xl font-bold text-gray-900">
            Payslip Details
          </h1>

          <p className="mt-2 text-gray-500">
            Review the saved payroll calculation for this employee.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-3 font-medium hover:bg-gray-50"
          >
            <FiPrinter />
            Print
          </button>

          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-2xl bg-gray-900 px-5 py-3 font-medium text-white hover:bg-black"
          >
            <FiDownload />
            Save as PDF
          </button>

          {payslip.status === "POSTED" ? (
            <button
              onClick={markPaid}
              disabled={isMarkingPaid}
              className="flex items-center gap-2 rounded-2xl bg-primary-700 px-5 py-3 font-medium text-white hover:bg-primary-800 disabled:opacity-50"
            >
              <FiCheckCircle />
              {isMarkingPaid ? "Marking..." : "Mark Paid"}
            </button>
          ) : null}

          <button
            disabled
            title="Email delivery will be enabled after the email service is added."
            className="flex cursor-not-allowed items-center gap-2 rounded-2xl bg-gray-100 px-5 py-3 font-medium text-gray-400"
          >
            <FiMail />
            Email Payslip
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl bg-gradient-to-r from-primary-800 to-primary-700 p-8 text-white">
        <div className="flex items-start justify-between gap-5">
          <div>
            <h2 className="text-3xl font-bold">Charms Collections</h2>

            <p className="mt-2 text-primary-100">
              Human Resource Management System
            </p>
          </div>

          <div className="text-right">
            <p className="text-sm text-primary-100">Payslip Status</p>

            <span className="mt-2 inline-block rounded-full bg-white/20 px-4 py-2 font-semibold">
              {statusLabel(payslip.status)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">
            Employee Information
          </h3>

          <div className="space-y-5">
            <InfoRow
              icon={<FiUser />}
              label="Employee Name"
              value={payslip.employeeNameSnapshot}
            />

            <InfoRow
              icon={<FiBriefcase />}
              label="Employee ID"
              value={payslip.employeeCodeSnapshot}
            />

            <InfoRow
              icon={<FiBriefcase />}
              label="Salary Type"
              value={readableValue(payslip.salaryTypeSnapshot)}
            />

            <InfoRow
              icon={<FiFileText />}
              label="Payroll Item Status"
              value={statusLabel(payslip.status)}
            />
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">
            Payroll Information
          </h3>

          <div className="space-y-5">
            <InfoRow
              icon={<FiCalendar />}
              label="Payroll Month"
              value={payrollMonth}
            />

            <InfoRow
              icon={<FiCalendar />}
              label="Calculation Through"
              value={formatDate(payslip.payrollRun.calculationThrough)}
            />

            <InfoRow
              icon={<FiCalendar />}
              label="Payment Date"
              value={formatDate(payslip.paidAt)}
            />

            <InfoRow
              icon={<FiCreditCard />}
              label="Payment Mode"
              value={readableValue(payslip.paymentModeSnapshot)}
            />

            <InfoRow
              icon={<FiCreditCard />}
              label="Payment Reference"
              value={payslip.paymentReference || "—"}
            />
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">Earnings</h3>

          <div className="space-y-5">
            <BreakdownRow
              label="Base Salary"
              amount={payslip.baseSalaryAmount}
            />

            <BreakdownRow label="Overtime" amount={payslip.overtimeAmount} />
            <BreakdownRow
              label={`Unused holiday payout (${numberValue(payslip.calculationSnapshot?.holidayEncashmentDays)} days)`}
              amount={payslip.calculationSnapshot?.holidayEncashmentAmount || 0}
            />

            <BreakdownRow
              label="POS Commission"
              amount={payslip.netCommissionAmount}
            />

            <BreakdownRow
              label="Manual Earnings / Bonus"
              amount={manualEarnings}
            />

            <div className="border-t border-gray-100 pt-5">
              <BreakdownRow
                label="Gross Pay"
                amount={payslip.grossEarnings}
                highlight
              />
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">
            Deductions
          </h3>

          <div className="space-y-5">
            <BreakdownRow
              label={`Unpaid Absence (${payslip.unpaidAbsentDays} days)`}
              amount={payslip.absenceDeductionAmount}
            />

            <BreakdownRow
              label={`Unpaid Leave (${payslip.unpaidLeaveDays} days)`}
              amount={payslip.unpaidLeaveDeductionAmount}
            />

            <BreakdownRow
              label={`Early Exit (${payslip.earlyExitMinutes} mins)`}
              amount={payslip.earlyExitDeductionAmount}
            />

            {manualDeductionsExcludingAdvance > 0 ? (
              <BreakdownRow
                label="Manual Deductions"
                amount={manualDeductionsExcludingAdvance}
              />
            ) : null}

            {advanceRecoveryAmount > 0 ? (
              <BreakdownRow
                label="Employee Advance Recovery"
                amount={advanceRecoveryAmount}
              />
            ) : null}

            <div className="border-t border-gray-100 pt-5">
              <BreakdownRow
                label="Total Deductions"
                amount={payslip.totalDeductions}
                highlight
                deduction
              />
            </div>
          </div>
        </section>
      </div>

      {payslip.adjustments?.length ? (
        <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-7 py-6">
            <h3 className="font-semibold text-gray-900">
              Adjustments and Recoveries
            </h3>
          </div>

          <div className="divide-y divide-gray-100">
            {payslip.adjustments.map((adjustment) => (
              <div
                key={adjustment.id}
                className="flex items-center justify-between gap-4 px-7 py-5"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {adjustmentLabel(adjustment.description)}
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    {readableValue(adjustment.type)}
                  </p>
                </div>

                <span
                  className={
                    adjustment.type === "DEDUCTION"
                      ? "font-bold text-red-600"
                      : "font-bold text-green-600"
                  }
                >
                  {adjustment.type === "DEDUCTION" ? "−" : "+"}
                  {formatMoney(adjustment.amount)}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="rounded-[2rem] bg-gradient-to-r from-primary-700 to-primary-800 p-10 text-white shadow-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            {advanceRecoveryAmount > 0 ? (
              <>
                <p className="text-sm uppercase tracking-wider text-primary-100">
                  Amount Payable Before Advance Recovery
                </p>

                <h2 className="mt-3 text-5xl font-bold">
                  {formatMoney(payoutBeforeAdvanceRecovery)}
                </h2>

                <div className="mt-5 rounded-2xl border border-white/20 bg-white/10 px-5 py-4">
                  <p className="text-sm text-primary-100">
                    Employee Advance Recovery
                  </p>

                  <p className="mt-1 text-2xl font-bold text-red-200">
                    −{formatMoney(advanceRecoveryAmount)}
                  </p>
                </div>

                <div className="mt-5 border-t border-white/20 pt-5">
                  <p className="text-sm uppercase tracking-wider text-primary-100">
                    Final Amount Payable
                  </p>

                  <p className="mt-2 text-3xl font-bold">
                    {formatMoney(payslip.netPayable)}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm uppercase tracking-wider text-primary-100">
                  Final Take Home Salary
                </p>

                <h2 className="mt-3 text-5xl font-bold">
                  {formatMoney(payslip.netPayable)}
                </h2>

                <p className="mt-3 text-primary-100">
                  Net salary payable for {payrollMonth}
                </p>
              </>
            )}
          </div>

          <div className="rounded-3xl bg-white/10 px-8 py-6 backdrop-blur-sm">
            <p className="text-sm text-primary-100">Payment Status</p>

            <p className="mt-2 text-2xl font-bold">
              {statusLabel(payslip.status)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
        {icon}
      </div>

      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="mt-1 font-semibold text-gray-900">{value || "—"}</p>
      </div>
    </div>
  );
}

function BreakdownRow({ label, amount, highlight = false, deduction = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={highlight ? "font-semibold text-gray-900" : "text-gray-600"}
      >
        {label}
      </span>

      <span
        className={
          highlight
            ? deduction
              ? "text-xl font-bold text-red-600"
              : "text-xl font-bold text-primary-700"
            : "font-semibold text-gray-900"
        }
      >
        {formatMoney(amount)}
      </span>
    </div>
  );
}
